import React from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Paper,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Visibility } from "@mui/icons-material";

interface StageSessionCardProps {
  booking: any;
}

const StageSessionCard: React.FC<StageSessionCardProps> = ({ booking }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // — Netflix-ish tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
    green: "#22c55e",
    blue: "#3b82f6",
    amber: "#f59e0b",
    gray: "#9ca3af",
  };

  const statusChipSx = (c: string) => ({
    fontWeight: 800,
    fontSize: 12.5,
    px: 1.5,
    height: 28,
    borderRadius: 2,
    color: c,
    backgroundColor: "rgba(255,255,255,0.06)",
    border: `1px solid ${c}`,
    letterSpacing: "-0.01em",
    backdropFilter: "blur(6px)",
  });

  const getStatusChip = () => {
    switch (booking.status) {
      case "Scheduled":
        return <Chip label="Accepted" sx={statusChipSx(COLORS.blue)} size="small" />;
      case "Ongoing":
        return <Chip label="Ongoing" sx={statusChipSx(COLORS.green)} size="small" />;
      case "Completed":
        return <Chip label="Completed" sx={statusChipSx(COLORS.gray)} size="small" />;
      case "Pending":
        return <Chip label="Pending" sx={statusChipSx(COLORS.amber)} size="small" />;
      case "Cancelled":
        return <Chip label="Cancelled" sx={statusChipSx(COLORS.red)} size="small" />;
      default:
        return null;
    }
  };

  const title = booking.stagecard?.title || "Untitled Stage Event";
  const talent = booking.talent?.username || "Unknown Talent";
  const avatar =
    booking.stagecard?.primary_thumbnail ||
    booking.talent?.username?.[0] ||
    "T";
  const date = dayjs(booking.start_date_time).format("DD MMM YYYY");
  const timeRange = `${dayjs(booking.start_date_time).format("hh:mm A")} - ${dayjs(
    booking.end_date_time
  ).format("hh:mm A")}`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        display: "flex",
        flexDirection: isSmall ? "column" : "row",
        alignItems: isSmall ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: { xs: 2, sm: 3 },
        border: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.panel,
        width: "100%",
        boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
        transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "rgba(229,9,20,0.45)",
          boxShadow: "0 24px 60px rgba(229,9,20,0.12)",
        },
      }}
    >
      {/* Left: Avatar & Info */}
      <Stack
        direction="row"
        spacing={2}
        alignItems={isSmall ? "flex-start" : "center"}
        flex={1}
        minWidth={0}
      >
        <Avatar
          variant="square"
          sx={{
            width: 56,
            height: 56,
            fontSize: 22,
            borderRadius: 2,
            bgcolor: COLORS.red,
            color: "#fff",
            backgroundImage: avatar?.startsWith("http")
              ? "none"
              : "linear-gradient(135deg, #E50914 0%, #b20710 100%)",
            boxShadow: avatar?.startsWith("http")
              ? "none"
              : "0 8px 24px rgba(229,9,20,0.45)",
            userSelect: "none",
            border: `1px solid ${COLORS.border}`,
          }}
          src={avatar?.startsWith("http") ? avatar : undefined}
        >
          {!avatar?.startsWith("http") && avatar}
        </Avatar>

        <Box minWidth={0}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 16, sm: 18 }}
            fontFamily="Lexend"
            title={title}
            sx={{ mb: 0.3, color: COLORS.text, letterSpacing: "-0.01em" }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            fontFamily="Lexend"
            title={`With: ${talent}`}
            sx={{ lineHeight: 1.6, color: COLORS.muted }}
          >
            With <Box component="strong" sx={{ color: COLORS.text }}>{talent}</Box>
          </Typography>
          <Typography
            variant="body2"
            fontFamily="Lexend"
            noWrap
            sx={{ opacity: 0.9, color: COLORS.muted }}
          >
            {date} | {timeRange}
          </Typography>
        </Box>
      </Stack>

      {/* Middle: Status */}
      <Box
        sx={{
          minWidth: 110,
          display: "flex",
          justifyContent: isSmall ? "flex-start" : "center",
          mt: isSmall ? 1 : 0,
        }}
      >
        {getStatusChip()}
      </Box>

      {/* Right: Action Buttons */}
      <Box
        mt={isSmall ? 2 : 0}
        minWidth={110}
        display="flex"
        justifyContent="flex-end"
        gap={1}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<Visibility />}
          onClick={() =>
            navigate(`/stage-booking/${booking.id}`, {
              state: { booking },
            })
          }
          sx={{
            borderRadius: 2,
            borderColor: COLORS.red,
            color: COLORS.red,
            padding: "8px 14px",
            textTransform: "none",
            fontFamily: "Lexend",
            fontWeight: 800,
            letterSpacing: "-0.01em",
            "&:hover": {
              borderColor: COLORS.redHover,
              backgroundColor: COLORS.red,
              color: "#fff",
              boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
            },
            "& .MuiButton-startIcon": {
              color: "inherit",
            },
          }}
        >
          View
        </Button>
      </Box>
    </Paper>
  );
};

export default StageSessionCard;
